/**
 * Timer + claim lifecycle for the win overlay, kept free of React so it can
 * be tested with fake timers.
 *
 * Guarantees:
 *  - the reward claim is requested at most once per flow;
 *  - "Continue" never skips the claim: pressed early, it claims immediately
 *    and closes as soon as the claim settles;
 *  - after dispose() (unmount) no timer fires and no callback runs, even if a
 *    claim response arrives later.
 */

export interface CelebrationTimings {
  /** Wait before claiming (lets the entrance animation play). */
  claimDelayMs: number;
  /** How long the result stays up before closing by itself. */
  resultMs: number;
}

/**
 * With reduced motion there is no animation to wait for, so the overlay only
 * stays long enough to read the result.
 */
export const celebrationTimings = (reduceMotion: boolean): CelebrationTimings =>
  reduceMotion
    ? { claimDelayMs: 400, resultMs: 2500 }
    : { claimDelayMs: 3500, resultMs: 3000 };

export type CelebrationPhase = 'presenting' | 'claiming' | 'claimed' | 'error';

export interface ClaimHandlers<T> {
  onSuccess: (data: T) => void;
  onError: (err: unknown) => void;
}

export interface CelebrationFlowOptions<T> {
  timings: CelebrationTimings;
  claim: (handlers: ClaimHandlers<T>) => void;
  onPhase: (phase: CelebrationPhase, data?: T) => void;
  onComplete: () => void;
}

export interface CelebrationFlow {
  start: () => void;
  /** User pressed Continue / Escape. */
  continueNow: () => void;
  dispose: () => void;
}

export function createCelebrationFlow<T>({
  timings,
  claim,
  onPhase,
  onComplete,
}: CelebrationFlowOptions<T>): CelebrationFlow {
  let phase: CelebrationPhase = 'presenting';
  let claimRequested = false;
  let closeRequested = false;
  let completed = false;
  let disposed = false;
  let delayTimer: ReturnType<typeof setTimeout> | undefined;
  let resultTimer: ReturnType<typeof setTimeout> | undefined;

  const clearTimers = () => {
    if (delayTimer) clearTimeout(delayTimer);
    if (resultTimer) clearTimeout(resultTimer);
    delayTimer = resultTimer = undefined;
  };

  const setPhase = (next: CelebrationPhase, data?: T) => {
    phase = next;
    onPhase(next, data);
  };

  const complete = () => {
    if (completed || disposed) return;
    completed = true;
    clearTimers();
    onComplete();
  };

  const settle = (next: 'claimed' | 'error', data?: T) => {
    if (disposed || completed) return;
    setPhase(next, data);
    if (closeRequested) {
      complete();
      return;
    }
    resultTimer = setTimeout(complete, timings.resultMs);
  };

  const runClaim = () => {
    if (claimRequested || disposed) return;
    claimRequested = true;
    if (delayTimer) clearTimeout(delayTimer);
    delayTimer = undefined;
    setPhase('claiming');
    claim({
      onSuccess: data => settle('claimed', data),
      onError: () => settle('error'),
    });
  };

  return {
    start: () => {
      if (disposed || claimRequested || delayTimer) return;
      delayTimer = setTimeout(runClaim, timings.claimDelayMs);
    },
    continueNow: () => {
      if (disposed || completed) return;
      closeRequested = true;
      if (!claimRequested) {
        runClaim();
        return;
      }
      if (phase === 'claimed' || phase === 'error') complete();
      // still claiming: settle() closes as soon as the response lands
    },
    dispose: () => {
      disposed = true;
      clearTimers();
    },
  };
}
